import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { API, authFetch, titleCase } from '../../constants';
import { ACTION_COLORS, Pagination } from './constants';
import DataTable, { Column } from '../../components/ui/DataTable';
import FilterBar from '../../components/ui/FilterBar';

export default function ActivityLogTab() {
  const { toast } = useToast();
  const [entries, setEntries] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const loadAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ org_id: '1', limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
      if (actionFilter) params.set('action', actionFilter);
      if (deptFilter) params.set('department', deptFilter);
      if (actorFilter) params.set('actor_type', actorFilter);
      const res = await authFetch(`${API}/audit-log?${params}`);
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        if (data.filters) setFilters(data.filters);
      }
    } catch (err) {
      console.error('Failed to load audit log:', err);
      toast({ message: 'Failed to load audit log.', type: 'error' });
    }
    setLoading(false);
  }, [actionFilter, deptFilter, actorFilter, page]);

  useEffect(() => { loadAuditLog(); }, [loadAuditLog]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const columns: Column[] = [
    {
      key: 'timestamp', label: 'Timestamp', width: 130,
      render: (v: any) => (
        <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap', fontSize: 10 }}>
          {v ? new Date(v).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '--'}
        </span>
      ),
    },
    {
      key: 'exception_id', label: 'Exc #', width: 70,
      render: (v: any) => (
        <span className="mono" style={{ fontWeight: 700, color: 'var(--navy-700)' }}>#{v}</span>
      ),
    },
    {
      key: 'action', label: 'Action', width: 160,
      render: (v: any) => {
        const actionLabel = (v || '').replace(/_/g, ' ').toUpperCase();
        const actionColor = ACTION_COLORS[v] || 'var(--text-secondary)';
        return (
          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: `${actionColor}20`, color: actionColor }}>
            {actionLabel}
          </span>
        );
      },
    },
    {
      key: 'actor_type', label: 'Actor', width: 140,
      render: (v: any, row: any) => (
        <span style={{ color: v === 'human' ? 'var(--navy-600)' : v === 'ai_agent' ? 'var(--navy-800)' : 'var(--text-secondary)', fontSize: 10 }}>
          {v}{row.user_id ? ` #${row.user_id}` : ''}{row.actor_id ? ` (${row.actor_id})` : ''}
        </span>
      ),
    },
    {
      key: 'department', label: 'Department', width: 120,
      render: (v: any) => (
        <span style={{ color: 'var(--text-secondary)', fontSize: 10 }}>{titleCase(v)}</span>
      ),
    },
    {
      key: 'amount', label: 'Amount', align: 'right' as const, width: 110,
      render: (v: any) => (
        <span className="mono" style={{ fontWeight: 700, color: v > 1000000 ? 'var(--red)' : v > 100000 ? 'var(--amber)' : 'var(--text-primary)' }}>
          ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      ),
    },
    {
      key: 'description', label: 'Description',
      render: (v: any) => (
        <span style={{ color: 'var(--text-secondary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: 10 }}>
          {v || '--'}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Filters */}
      <FilterBar
        filters={[
          {
            key: 'action', label: 'Actions', type: 'select', value: actionFilter,
            onChange: (v) => { setActionFilter(v); setPage(0); },
            placeholder: 'All Actions',
            options: (filters.action_types || []).map((a: string) => ({ value: a, label: a.replace(/_/g, ' ').toUpperCase() })),
          },
          {
            key: 'department', label: 'Departments', type: 'select', value: deptFilter,
            onChange: (v) => { setDeptFilter(v); setPage(0); },
            placeholder: 'All Departments',
            options: (filters.departments || []).map((d: string) => ({ value: d, label: titleCase(d) })),
          },
          {
            key: 'actor', label: 'Actors', type: 'select', value: actorFilter,
            onChange: (v) => { setActorFilter(v); setPage(0); },
            placeholder: 'All Actors',
            options: [{ value: 'system', label: 'System' }, { value: 'ai_agent', label: 'AI Agent' }, { value: 'human', label: 'Human' }],
          },
        ]}
      />

      <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, marginTop: -8 }}>
        {total} entries
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        rows={entries}
        loading={loading}
        emptyMessage="No audit entries found"
        rowKey="id"
      />

      {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onPage={setPage} />}
    </div>
  );
}
